
import { computed, defineComponent, onMounted } from 'vue';
import { useStore } from 'vuex';
import { onBeforeRouteUpdate, RouteLocationNormalized, useRoute, useRouter } from 'vue-router';
import { ROUTE_PARAMS_TYPE } from '@/router';
import { hasRouterQuery, removeRouterQuery } from '@/router/flags';
import { useAnalytics } from '@/plugins/analytics';
import { useCookieConsent } from '@/helpers/useCookieConsent';
import { getCompanyId } from '@/helpers/useCompanyIds';
import { OrganizationMode } from '@/gql/graphql';

const ACCESS_DENIED_MESSAGE = 'access_denied';

export default defineComponent({
  name: 'CabinetView',
  props: {
    id: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const analytics = useAnalytics();
    const router = useRouter();
    const route = useRoute();

    const rootStore = useStore();
    const company = computed(() => rootStore.getters['cabinet/company']);
    const getCompanyRouteTypeById = computed(() => rootStore.getters['cabinet/getCompanyRouteTypeById']);

    useCookieConsent();

    /**
     * Проверка изменения компании, при необходимости подгрузка и проверка доступа
     */
    const checkCompanyChanged = async (id: string, toRoute: RouteLocationNormalized) => {
      const ids =
        props.type === ROUTE_PARAMS_TYPE.department
          ? [company.value?.partner?.id]
          : [company.value?.id, company.value?.contract?.id];

      const isNotChanged = ids.includes(id);
      if (isNotChanged) {
        return;
      }

      // если изменилась - грузим новую
      const companyType = toRoute.params.type as string;
      console.log('cabinet/loadCompany', id, companyType);

      await rootStore.dispatch('cabinet/loadCompany', getCompanyId({ id, type: companyType }));

      // теперь проверить роли
      const { type, role, partnersCount } = company.value || {};
      const { types, roles } = toRoute.meta['nav'] || {};
      if ((types === undefined || types.includes(type)) && (roles === undefined || roles.includes(role))) {
        if (!['partners', 'partner'].includes(toRoute.name as string) || partnersCount > 1) {
          return;
        }
      }

      // сюда пришли, что-то не то с доступом
      throw new Error(ACCESS_DENIED_MESSAGE);
    };

    onBeforeRouteUpdate((to, _, next) => {
      /**
       * При обновлении маршрутизатора проверяем доступность выбранной страницы
       */
      const routeId = to.params.id as string;
      checkCompanyChanged(routeId, to)
        .then(() => {
          if (to.name !== 'termsOfUse' && company.value.mode === OrganizationMode.Inactive) {
            // нужно передать id компании, иначе страница не изменится
            next({ name: 'termsOfUse', params: { id: company.value.id } });
            return;
          }
          next();
        })
        .catch((err) => {
          console.log(err.message);

          if (err.message === ACCESS_DENIED_MESSAGE) {
            if (to.redirectedFrom) {
              next();
              return;
            }
            next({ name: 'orders', params: { id: routeId, type: getCompanyRouteTypeById.value(routeId) } });
          } else {
            next(false);
          }
        });
    });

    onMounted(async () => {
      await checkCompanyChanged(props.id, route)
        .then(() => {
          if (route.name !== 'termsOfUse' && company.value.mode === OrganizationMode.Inactive) {
            router.replace({ name: 'termsOfUse' });
          }
        })
        .catch((err) => {
          if (err.message === ACCESS_DENIED_MESSAGE) {
            router.replace({
              name: 'orders',
              params: { id: props.id, type: getCompanyRouteTypeById.value(props.id) },
            });
          }
        });

      if (hasRouterQuery('loginByPhoneSuccess')) {
        analytics.handle('loginSuccess', { id: props.id, method: 'phone' });
        removeRouterQuery('loginByPhoneSuccess');
      } else if (hasRouterQuery('loginByEmailSuccess')) {
        analytics.handle('loginSuccess', { id: props.id, method: 'email' });
        removeRouterQuery('loginByEmailSuccess');
      } else if (hasRouterQuery('registrationSuccess')) {
        analytics.handle('registrationSuccess', { id: props.id, method: 'phone' });
        removeRouterQuery('registrationSuccess');
      }
    });

    return {
      company,
    };
  },
});
